import {DiagnosisCode} from 'src/contracts/diagnosisCode';
import { GrouperData } from 'src/contracts/grouperData';
import { PatientData } from 'src/contracts/patientData';
import {
  DiagnosisResultDto, DrgInfoDto,
  GetGrouperDataResponse, GrouperInfoDto,
  GrouperPatientInfo,
  ProcedureResultDto
} from 'src/contracts/getGrouperDataResponse';
import { isEmpty } from 'src/common/arrayUtil';
import {GrouperInfo} from 'src/contracts/grouperInfo';
import {DrgInfo} from 'src/contracts/drgInfo';
import {DrgAnalysisCode, DrgAnalysis, Requirement, Suggestion} from 'src/contracts/drgAnalysis';
import {AnalyseCode, AnalyseCodeList, AnalyseResponseRequirement, AnalyseResponseSuggestion, DrgAnalyseResponseDto} from 'src/contracts/drgAnalyseResponseDto';
import { Code } from 'src/contracts/code';
import {createDxCodeWithChipInfo} from 'src/common/code-helper';

export class GrouperServiceConverter {

  public static convertGrouperData(response: GetGrouperDataResponse): GrouperData | undefined {
    if (!response) {
      console.log('Undefined response');
      return undefined;
    }

    return {
      grouperInfo: this.getGrouperInfo(response.grouper_info),
      drgInfo: response.drg_info || [],
      patient: this.getPatient(response.patient),
      diagnosisCodes: this.getDiagnosisCodes(response.diagnoses, response.grouper_info?.grouper_source?.grouper_type),
      procedureCodes: this.getProcedureCodes(response.procedures),
      draft_data: response.draft_data
    };
  }

  public static convertDrgAnalyseData(response: DrgAnalyseResponseDto): DrgAnalysis | undefined {
    if (!response) {
      console.log('Undefined response');
      return undefined;
    }

    return {
      initialDrg: response.initial_drg,
      suggestions: this.getDrgAnalysisSuggestions(response.suggestions??[]),
    };
  }

  private static getGrouperInfo(grouperInfoDto: GrouperInfoDto): GrouperInfo {
    if (!grouperInfoDto) {
      throw new Error('GrouperInfo data not found');
    }

    return {
      medicare_provider_number: grouperInfoDto.medicare_provider_number,
      grouper_state: grouperInfoDto.grouper_state || [],
      grouper_source: grouperInfoDto.grouper_source
    }
  }


  private static getPatient(patientData: GrouperPatientInfo): PatientData {
    if (!patientData) {
      throw new Error('Patient data not found');
    }

    return {
      name: patientData.patient_full_name,
      dischargeDate: patientData.discharge_date || 'none',
      sex: patientData.sex,
      codingSystem: patientData.coding_system,
      los: patientData.los
    }
  }

  private static getProcedureCodes(procedures: ProcedureResultDto[]): Code[] {
    if (isEmpty(procedures)) {
      return [];
    }

    return procedures.map((procedure: ProcedureResultDto) => {
      return <Code>{
        id: procedure.position,
        code: procedure.code,
        description: procedure.description,
        drgModifier: procedure?.flags?.includes('OR') ? ['OR'] : []
      }
    })
  }

  private static getDiagnosisCodes(diagnoses: DiagnosisResultDto[], grouperType: string): DiagnosisCode[] {
    if (isEmpty(diagnoses)) {
      return [];
    }
    return diagnoses.map((diagnosis: DiagnosisResultDto, index: number) => {
      return createDxCodeWithChipInfo(diagnosis.position, diagnosis.code, index == 0, diagnosis.description, diagnosis.poa?.trim(),
          diagnosis.flags?.includes('MCC'), diagnosis.flags?.includes('CC'), diagnosis.flags?.includes('HAC'),
          diagnosis.hcc, diagnosis.elixhauser_flags, diagnosis.soi_level, diagnosis.rom_level, grouperType === 'APR');
    })
  }

  private static getDrgInfos(drgInfoDtos: DrgInfoDto[]): DrgInfo[] {
    if (isEmpty(drgInfoDtos)) {
      return [];
    }

    return drgInfoDtos.map((drgInfoDto: DrgInfoDto) => {
      return <DrgInfo>{
        grouperVersion: drgInfoDto.grouper_version,
        soi: drgInfoDto.severity_of_illness,
        rom: drgInfoDto.risk_of_mortality,
        drgCode: drgInfoDto.drg,
        gmlos: drgInfoDto.gmlos,
        mdc: drgInfoDto.mdc,
        drgReimbursement: drgInfoDto.drg_reimbursement,
        grouperType: drgInfoDto.grouper_type,
        pricerType: drgInfoDto.pricer_type,

        dischargeStatus: drgInfoDto.discharge_status
      }
    })
  }

  private static getDrgAnalysisSuggestions(suggestions: AnalyseResponseSuggestion[]): Suggestion[] {
    if (isEmpty(suggestions)) {
      return [];
    }

    return suggestions.map((suggestion: AnalyseResponseSuggestion) => {
      return <Suggestion>{
        drg: {
          suggestedDrg: suggestion.suggested_drg,
          weight: suggestion.drg_weight,
          description: suggestion.drg_description,
        },
        requirements: this.getDrgAnalyseRequirement(suggestion.requirements)
      }
    })
  }

  private static getDrgAnalyseRequirement(requirements: AnalyseResponseRequirement[]): Requirement[] {
    if (isEmpty(requirements)) {
      return [];
    }

    return requirements.map((requirement: AnalyseResponseRequirement) => {
      return <Requirement>{
        description: requirement.description,
        codelist: this.getCodeList(requirement.codelists)
      }
    })
  }

  private static getCodeList(codes: AnalyseCodeList[]): DrgAnalysisCode[] {
    if (isEmpty(codes)) {
      return [];
    }
    return  codes.flatMap((codeList: AnalyseCodeList) => {
      return codeList.codelist
    }).map((code: AnalyseCode) => {
      return <DrgAnalysisCode>{
        codeType: code.code_type,
        value: code.value,
        shortDescription: code.short_description,
        mediumDescription: code.medium_description,
        effective_date: code.effective_date
      }
    })
  }

  public static convertCodes(codes: AnalyseCode[]): DrgAnalysisCode[] {
    if (isEmpty(codes)) {
      return [];
    }
    return codes.map((code: AnalyseCode) => {
      return <DrgAnalysisCode>{
        codeType: code.code_type,
        value: code.value,
        shortDescription: code.short_description,
        mediumDescription: code.medium_description,
        effective_date: code.effective_date
      }
    })
  }
}
